export const JST_TIME_ZONE = "Asia/Tokyo";
export const PLANNED_START_MINUTES = 8 * 60;
export const PLANNED_END_MINUTES = 17 * 60;
export const STANDARD_WORK_MINUTES = 8 * 60;

export type WorkStatus = "work" | "day_off" | "paid_leave" | "holiday_work";

export interface DayRecord {
  workDate: string;
  status: WorkStatus | null;
  scheduleText: string;
  plannedStartMinutes: number;
  plannedEndMinutes: number;
  actualStartMinutes: number | null;
  actualEndMinutes: number | null;
  breakMinutes: 60 | 90;
  breakIsManual: boolean;
  actualConfirmed: boolean;
  revision: number;
  source: "app" | "import";
}

export interface MonthSettings {
  monthKey: string;
  requiredDaysOff: number | null;
  finalizedAt: string | null;
  lastExportedAt: string | null;
  revision: number;
}

export interface LeavePeriod {
  periodStart: string;
  grantedDays: number | null;
  startingUsedDays: number | null;
  revision: number;
}

export interface Profile {
  displayName: string;
  workplace: string;
  leaveRecipient: string;
  leaveOrganization: string;
  leaveDepartment: string;
}

export interface LeaveSummary {
  periodStart: string;
  periodEnd: string;
  grantedDays: number | null;
  startingUsedDays: number | null;
  usedDays: number;
  plannedDays: number;
}

export interface MonthPayload {
  profile: Profile;
  month: MonthSettings;
  days: DayRecord[];
  leavePeriods: LeavePeriod[];
  leaveSummary: LeaveSummary;
  today: string;
}

export interface WorkTotals {
  workedMinutes: number;
  overtimeMinutes: number;
}

export function calculateWorkTotals(record: Pick<DayRecord, "status" | "actualStartMinutes" | "actualEndMinutes" | "breakMinutes">): WorkTotals {
  if (
    (record.status !== "work" && record.status !== "holiday_work") ||
    record.actualStartMinutes === null ||
    record.actualEndMinutes === null ||
    record.actualEndMinutes <= record.actualStartMinutes
  ) {
    return { workedMinutes: 0, overtimeMinutes: 0 };
  }

  const workedMinutes = Math.max(0, record.actualEndMinutes - record.actualStartMinutes - record.breakMinutes);
  const overtimeMinutes = record.status === "holiday_work"
    ? workedMinutes
    : Math.max(0, workedMinutes - STANDARD_WORK_MINUTES);
  return { workedMinutes, overtimeMinutes };
}

export function automaticBreakMinutes(startMinutes: number | null, endMinutes: number | null): 60 | 90 {
  if (startMinutes === null || endMinutes === null || endMinutes <= startMinutes) return 60;
  return endMinutes - startMinutes > 11 * 60 ? 90 : 60;
}

export function isSummerDefaultMonth(monthIndexOneBased: number): boolean {
  return monthIndexOneBased >= 4 && monthIndexOneBased <= 11;
}

export function formatMinutesAsTime(minutes: number | null): string {
  if (minutes === null) return "--:--";
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return `${String(hours).padStart(2, "0")}:${String(rest).padStart(2, "0")}`;
}

export function formatDuration(minutes: number): string {
  const sign = minutes < 0 ? "−" : "";
  const absolute = Math.abs(minutes);
  const hours = Math.floor(absolute / 60);
  const rest = absolute % 60;
  return `${sign}${hours}時間${rest ? `${rest}分` : ""}`;
}

export function daysInMonth(monthKey: string): number {
  const [year, month] = monthKey.split("-").map(Number);
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

export function enumerateMonthDates(monthKey: string): string[] {
  return Array.from({ length: daysInMonth(monthKey) }, (_, index) => `${monthKey}-${String(index + 1).padStart(2, "0")}`);
}

export function currentJstDate(now = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: JST_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
}

export function leavePeriodStartForDate(date: string): string {
  const [year, month, day] = date.split("-").map(Number);
  const startYear = month > 2 || (month === 2 && day >= 15) ? year : year - 1;
  return `${startYear}-02-15`;
}

export function validateMonthKey(value: string): boolean {
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(value)) return false;
  const year = Number(value.slice(0, 4));
  return year >= 2000 && year <= 2200;
}

export function validateDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
}
